import React, { useState } from 'react';

const StepThreeDesign = ({ data, onChange, errors }) => {
  const [imagePreview, setImagePreview] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
    
    // Clear error when user starts typing
    if (errors[name]) {
      errors[name] = '';
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageUrl = e.target.result;
          setImagePreview(imageUrl);
          onChange({ referenceImage: imageUrl });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    onChange({ referenceImage: null });
  };

  return (
    <div className="space-y-6">
      {/* Style Preference */}
      <div>
        <label htmlFor="stylePreference" className="block text-sm font-medium mb-2" style={{color: '#3E3E3E'}}>
          Style Preference *
        </label>
        <select
          id="stylePreference"
          name="stylePreference"
          value={data.stylePreference}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors appearance-none bg-white ${
            errors.stylePreference ? 'border-red-500' : 'border-stone-300'
          }`}
          style={{focusRingColor: '#C8A96A'}}
        >
          <option value="">Select style preference</option>
          <option value="modern">Modern</option>
          <option value="minimal">Minimal</option>
          <option value="luxury">Luxury</option>
          <option value="wooden">Wooden</option>
          <option value="classic">Classic</option>
        </select>
        {errors.stylePreference && (
          <p className="mt-1 text-sm text-red-500">{errors.stylePreference}</p>
        )}
      </div>

      {/* Color Preference */}
      <div>
        <label htmlFor="colorPreference" className="block text-sm font-medium mb-2" style={{color: '#3E3E3E'}}>
          Color Preference (Optional)
        </label>
        <input
          type="text"
          id="colorPreference"
          name="colorPreference"
          value={data.colorPreference}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
          placeholder="e.g., Warm colors, Neutral tones, Blue and white"
          style={{focusRingColor: '#C8A96A'}}
        />
        <p className="mt-1 text-sm text-stone-500">Tell us about your preferred color scheme</p>
      </div>

      {/* Reference Image Upload */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{color: '#3E3E3E'}}>
          Reference Image (Optional)
        </label>
        
        {!imagePreview ? (
          <div className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center hover:border-primary transition-colors cursor-pointer"
               style={{backgroundColor: '#F5EFE6'}}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="referenceImageUpload"
            />
            <label htmlFor="referenceImageUpload" className="cursor-pointer">
              <span className="material-symbols-outlined text-4xl mb-4" style={{color: '#C8A96A'}}>
                cloud_upload
              </span>
              <div>
                <p className="font-medium mb-2" style={{color: '#3E3E3E'}}>
                  Upload reference image
                </p>
                <p className="text-sm text-stone-500">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            </label>
          </div>
        ) : (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Reference"
              className="w-full h-64 object-cover rounded-xl"
            />
            <button
              onClick={removeImage}
              className="absolute top-4 right-4 p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        )}
        
        <p className="mt-2 text-sm text-stone-500">
          Share any design inspiration or reference images
        </p>
      </div>

      {/* Style Preview Cards */}
      <div>
        <h3 className="text-lg font-medium mb-4" style={{color: '#3E3E3E'}}>Popular Styles</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg border border-stone-200 hover:border-primary transition-colors cursor-pointer">
            <div className="w-full h-20 bg-gradient-to-br from-gray-100 to-gray-300 rounded-lg mb-2"></div>
            <h4 className="font-medium text-sm" style={{color: '#3E3E3E'}}>Modern</h4>
            <p className="text-xs text-stone-600">Clean lines, minimal</p>
          </div>
          
          <div className="p-4 rounded-lg border border-stone-200 hover:border-primary transition-colors cursor-pointer">
            <div className="w-full h-20 bg-gradient-to-br from-stone-100 to-stone-300 rounded-lg mb-2"></div>
            <h4 className="font-medium text-sm" style={{color: '#3E3E3E'}}>Minimal</h4>
            <p className="text-xs text-stone-600">Simple, functional</p>
          </div>
          
          <div className="p-4 rounded-lg border border-stone-200 hover:border-primary transition-colors cursor-pointer">
            <div className="w-full h-20 bg-gradient-to-br from-amber-100 to-amber-300 rounded-lg mb-2"></div>
            <h4 className="font-medium text-sm" style={{color: '#3E3E3E'}}>Luxury</h4>
            <p className="text-xs text-stone-600">Elegant, premium</p>
          </div>
          
          <div className="p-4 rounded-lg border border-stone-200 hover:border-primary transition-colors cursor-pointer">
            <div className="w-full h-20 bg-gradient-to-br from-orange-100 to-orange-300 rounded-lg mb-2"></div>
            <h4 className="font-medium text-sm" style={{color: '#3E3E3E'}}>Wooden</h4>
            <p className="text-xs text-stone-600">Natural, warm</p>
          </div>
          
          <div className="p-4 rounded-lg border border-stone-200 hover:border-primary transition-colors cursor-pointer">
            <div className="w-full h-20 bg-gradient-to-br from-blue-100 to-blue-300 rounded-lg mb-2"></div>
            <h4 className="font-medium text-sm" style={{color: '#3E3E3E'}}>Classic</h4>
            <p className="text-xs text-stone-600">Timeless, elegant</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepThreeDesign;
